import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppShell } from "../components/layout/AppShell";
import { useAuth } from "../context/AuthContext";
import { MANAGER_ROLES } from "../utils/role";
import { useAttendanceData } from "../hooks/useAttendanceData";
import { useCamera } from "../hooks/useCamera";
import { CheckInBar } from "../features/attendance/CheckInBar";
import { HomeFaceCapture } from "../features/attendance/HomeFaceCapture";
import { TodayCard } from "../features/attendance/TodayCard";
import { WeatherCard } from "../features/attendance/WeatherCard";
import { useWeather } from "../api/weather";
import { useMobileReminders } from "../hooks/useMobileReminders";
import { DashboardSection } from "../features/attendance/DashboardSection";
import { useCheckFlow } from "../features/attendance/useCheckFlow";
import { FaceEnrollButton } from "../features/admin/FaceEnrollButton";
import { MyDutyCard } from "../features/duty/MyDutyCard";
import { LowBalanceBanner } from "../features/wallet/LowBalanceBanner";
import { BiometricGate } from "../features/legal/BiometricGate";
import { useBiometricGate } from "../features/legal/useBiometricGate";
import { useRemoteStatus } from "../hooks/useRemoteStatus";
import { RemoteStatusCard } from "../features/attendance/RemoteStatusCard";

const MANAGERS = MANAGER_ROLES;

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();  const data = useAttendanceData();
  const [dutyId, setDutyId] = useState("");
  const flow = useCheckFlow({ branchId: data.branchId, dutyId, onDone: data.refresh });
  const camera = useCamera(flow.cameraAllowed);
  const weather = useWeather(data.branch, i18n.language);
  useMobileReminders(data.branch, data.today, data.day);
  const bio = useBiometricGate();
  const remote = useRemoteStatus(user, data.branch);

  useEffect(() => {
    const open = () => {
      if (remote.active) { toast.message(t("employees.remoteNoAttendance")); return; }
      if (data.leaveType) { toast.message(t("leaveStatus.onLeaveToast")); return; }
      // يوم مُغلق غياباً: لا كاميرا ولا تسجيل — نُخبره بالسبب فقط
      if (data.dayClosed || !data.nextAction) {
        toast.error(t(data.dayClosed ? "check.dayClosedToast" : "check.doneToast"));
        return;
      }
      bio.guard(() => flow.start(data.nextAction));
    };
    window.addEventListener("helcoo:checkin", open);
    return () => window.removeEventListener("helcoo:checkin", open);
  }, [flow, data.nextAction, data.dayClosed, data.leaveType, bio, t, remote]);

  return (
    <AppShell>
      <div className="grid gap-6">
        <LowBalanceBanner />
        <WeatherCard branch={data.branch} weather={weather} />
        <MyDutyCard dutyId={dutyId} onSelect={setDutyId} />
        {!remote.active && !user?.face_enrolled ? (
          <div className="rounded-2xl border border-destructive/40 px-4 py-3 shadow-neu-sm" data-testid="self-enroll-card">
            <p className="text-sm text-destructive">{t("check.notEnrolled")}</p>
            <div className="mt-3">
              <FaceEnrollButton userId={user.id} enrolled={false} guard={bio.guard} onDone={() => window.location.reload()} />
            </div>
          </div>
        ) : null}
        {remote.active ? (
          <RemoteStatusCard active />
        ) : (
          <>
            {remote.pending ? <RemoteStatusCard since={remote.since} absentToday={data.dayClosed} /> : null}
            <TodayCard record={data.today} branch={data.branch} day={data.day} />
          </>
        )}
        {MANAGERS.includes(user?.role) ? <DashboardSection /> : null}
        {!remote.active ? (
          <CheckInBar
            branches={data.branches}
            branchId={data.branchId}
            setBranchId={data.setBranchId}
            nextAction={data.nextAction}
            day={data.day}
            absent={data.dayClosed}
            leaveType={data.leaveType}
            geo={flow.geo}
            geoError={flow.geoError}
            locating={flow.locating}
          />
        ) : null}
      </div>

      {!remote.active ? <HomeFaceCapture flow={flow} camera={camera} /> : null}
      <BiometricGate {...bio.gate} />
    </AppShell>
  );
}
